import { type ReactElement, useMemo } from 'react'
import { TDrawer } from 'app/components/terros'
import { PermissionTooltip } from 'app/components/permissions'
import { ButtonEdit, type ButtonEditProps } from 'app/components/buttons'
import { isNotDefined, type TeamId } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { getInitialism, useTeam, useTeamAvatarUploadUrl } from '@front/model'
import { UpdateTeamAvatarButton } from '../UpdateAvatarButton'
import { RestoreTeamButton } from '../RestoreTeamButton'
import { TeamEditFormPanel } from './TeamEditFormPanel'

type Props = Omit<ButtonEditProps, 'onClick'> & {
  teamId: TeamId
}
export const EditTeamButton = ({ teamId, ...props }: Props): ReactElement | null => {
  const [opened, { toggle, close }] = useDisclosure(false)
  const { data } = useTeam(teamId)
  const { data: upload } = useTeamAvatarUploadUrl(teamId, opened)
  const teamName = useMemo(() => data?.team.name ?? 'team', [data])
  const title = useMemo(() => `Edit ${teamName}`, [teamName])

  if (!data) return null
  if (data.team.isDeleted) return <RestoreTeamButton teamId={teamId} />

  return (
    <>
      <PermissionTooltip permissionType='team:update' targetId={teamId}>
        <ButtonEdit onClick={toggle} {...props} />
      </PermissionTooltip>
      <TDrawer
        isChildDrawer
        headerActions={
          <UpdateTeamAvatarButton
            avatarBlurhash={data?.team.avatarBlurhash}
            avatarUrl={data?.team.avatarUrl}
            disabled={isNotDefined(upload?.uploadAvatarUrl)}
            initials={getInitialism(data?.team.name ?? '')}
            teamId={teamId}
            uploadAvatarUrl={upload?.uploadAvatarUrl}
          />
        }
        opened={opened}
        size='lg'
        title={title}
        onClose={close}
      >
        <TeamEditFormPanel teamId={teamId} onSaved={close} />
      </TDrawer>
    </>
  )
}
